import type { MatchEndBroadcastMsg } from "@hupper/shared/protocol";

/** Local player's end-of-match stats for the left-hand stats card. */
export interface MatchEndStats {
  kills: number;
  deaths: number;
  /** Total match duration in seconds (start → deciding moment). */
  durationSec: number;
  name?: string;
  team?: "ct" | "t";
}

/** 754 → "12:34"; hour-long matches roll over to "1:02:34". */
function fmtDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

/**
 * End-of-match result screen. A transparent layer over {@link
 * MatchEndCinematic} (the local player's mannequin, centred and aimed at the
 * camera): the white VICTORY / DEFEAT title at the top, two cards on the left
 * (kills / deaths / match time, and the pot / fee / money won), and the "Back to
 * lobby" button below the mannequin. Posts the result up to the embedding
 * window and asks the parent to tear the iframe down on exit.
 */
export class MatchEndOverlay {
  private root: HTMLDivElement;
  private shown = false;
  private won = false;
  /** Settle result that resolved before the overlay was shown (during the
   *  post-match grace window) — applied when show() finally runs. */
  private pendingSettle: { kind: "ok" | "failed"; detail?: string } | null = null;

  constructor() {
    const el = document.createElement("div");
    el.id = "match-end-overlay";
    el.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9999",
      "display:none",
      // Transparent + click-through; the 3D mannequin shows underneath and
      // only the "Back to lobby" button re-enables pointer events.
      "pointer-events:none",
      "font-family:'Inter',ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
      "color:#F5F5F0",
    ].join(";");
    document.body.appendChild(el);
    this.root = el;
  }

  show(msg: MatchEndBroadcastMsg, stats?: MatchEndStats): void {
    if (this.shown) return;
    this.shown = true;

    const win = msg.won;
    this.won = win;

    const FONT = "'Inter',ui-sans-serif,system-ui,sans-serif";
    const title = win ? "VICTORY" : "DEFEAT";
    const accent = win ? "#34D399" : "#EF4444";
    const moneyLine = win ? `+$${msg.payout.toFixed(2)}` : `−$${msg.stake.toFixed(2)}`;
    const potLine = `$${(msg.stake * 2).toFixed(2)}`;
    const feeLine = `$${msg.fee.toFixed(2)}`;
    // Settlement status only — the pot/fee live in their own rows now. When
    // escrowed, `setSettleResult` rewrites this to paid / failed.
    const escrowLine = msg.escrowed ? "Settling on-chain…" : "Free play — no on-chain payout";

    const k = stats?.kills ?? 0;
    const d = stats?.deaths ?? 0;
    const time = fmtDuration(stats?.durationSec ?? 0);
    const who = stats?.name ? esc(stats.name) : "You";
    const isT = stats?.team === "t";
    const facColor = isT ? "#ff7a33" : "#5b8def";
    const faction = isT ? "Terrorist" : "Counter-Terrorist";

    this.root.innerHTML = `
      <style>
        #match-end-overlay .me-vig{position:absolute;inset:0;background:
          radial-gradient(ellipse 72% 62% at 50% 52%, rgba(8,6,5,0) 40%, rgba(8,6,5,0.55) 100%),
          linear-gradient(180deg, rgba(8,6,5,0.55) 0%, rgba(8,6,5,0) 18%, rgba(8,6,5,0) 78%, rgba(8,6,5,0.6) 100%);}
        #match-end-overlay .me-title{position:absolute;top:5.5%;left:0;right:0;text-align:center;
          font-size:54px;font-weight:800;letter-spacing:.05em;color:#fff;
          text-shadow:0 4px 26px rgba(0,0,0,0.6);animation:me-fade .5s ease both;}
        #match-end-overlay .me-bar{position:absolute;top:calc(5.5% + 72px);left:50%;width:66px;height:4px;
          border-radius:2px;transform:translateX(-50%);background:linear-gradient(90deg,#FF6B00,#FF8533);
          animation:me-fade .65s ease both;}
        #match-end-overlay .me-cards{position:absolute;left:4.5%;top:50%;transform:translateY(-50%);
          display:flex;flex-direction:column;gap:14px;width:262px;}
        #match-end-overlay .me-card{position:relative;overflow:hidden;padding:18px 20px;border-radius:16px;
          background:linear-gradient(180deg, rgba(28,24,21,0.90), rgba(14,11,9,0.94));
          border:1px solid rgba(255,255,255,0.10);box-shadow:0 20px 60px rgba(0,0,0,0.5);
          animation:me-inL .5s ease both;}
        #match-end-overlay .me-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;
          background:linear-gradient(90deg,#FF6B00,#FF8533);}
        #match-end-overlay .me-ch{display:flex;flex-direction:column;gap:3px;margin-bottom:14px;
          padding-bottom:12px;border-bottom:1px solid rgba(255,255,255,0.08);}
        #match-end-overlay .me-name{font-size:19px;font-weight:700;color:#fff;letter-spacing:.2px;}
        #match-end-overlay .me-fac{font-family:${FONT};font-size:10.5px;letter-spacing:2.5px;
          text-transform:uppercase;color:${facColor};}
        #match-end-overlay .me-ct{font-family:${FONT};font-size:10.5px;letter-spacing:2.5px;
          text-transform:uppercase;color:rgba(255,107,0,0.85);margin-bottom:10px;}
        #match-end-overlay .me-row{display:flex;align-items:baseline;justify-content:space-between;
          gap:18px;padding:6px 0;}
        #match-end-overlay .me-row-l{font-family:${FONT};font-size:11px;letter-spacing:2px;
          text-transform:uppercase;color:#A1A1A6;}
        #match-end-overlay .me-row-v{font-family:${FONT};font-size:28px;font-weight:700;line-height:1;
          color:#fff;font-variant-numeric:tabular-nums;}
        #match-end-overlay .me-row-v.sm{font-size:18px;}
        #match-end-overlay .me-escrow{margin-top:12px;padding-top:12px;
          border-top:1px solid rgba(255,255,255,0.08);
          font-family:${FONT};font-size:11px;letter-spacing:.02em;line-height:1.4;color:#A1A1A6;}
        #match-end-overlay .me-back{position:absolute;left:50%;bottom:5.5%;transform:translateX(-50%);
          pointer-events:auto;padding:17px 64px;
          font-family:'Inter',ui-sans-serif,system-ui,sans-serif;font-size:18px;font-weight:600;
          border:0;border-radius:14px;cursor:pointer;color:#fff;
          background:linear-gradient(180deg,#FF6B00,#F05500);box-shadow:0 12px 34px rgba(255,107,0,0.34);
          transition:filter 150ms ease,transform 80ms ease;}
        @keyframes me-inL{0%{opacity:0;transform:translateX(-22px)}100%{opacity:1;transform:translateX(0)}}
        @keyframes me-fade{0%{opacity:0}100%{opacity:1}}
      </style>
      <div class="me-vig"></div>
      <div class="me-title">${title}</div>
      <div class="me-bar"></div>
      <div class="me-cards">
        <div class="me-card">
          <div class="me-ch"><span class="me-name">${who}</span><span class="me-fac">${faction}</span></div>
          <div class="me-row"><span class="me-row-l">Kills</span><span class="me-row-v">${k}</span></div>
          <div class="me-row"><span class="me-row-l">Deaths</span><span class="me-row-v">${d}</span></div>
          <div class="me-row"><span class="me-row-l">Time</span><span class="me-row-v">${time}</span></div>
        </div>
        <div class="me-card">
          <div class="me-ct">Payout</div>
          <div class="me-row"><span class="me-row-l">Pot</span><span class="me-row-v sm">${potLine}</span></div>
          <div class="me-row"><span class="me-row-l">Fee</span><span class="me-row-v sm">${feeLine}</span></div>
          <div class="me-row"><span class="me-row-l">${win ? "Won" : "Lost"}</span><span class="me-row-v sm" style="color:${accent}">${moneyLine}</span></div>
          <div id="match-end-escrow" class="me-escrow">${escrowLine}</div>
        </div>
      </div>
      <button id="match-end-back" class="me-back" type="button">Back to lobby</button>
    `;
    this.root.style.display = "block";

    // Release the mouse so the player can click the button.
    try {
      document.exitPointerLock?.();
    } catch {
      /* ignore */
    }

    const backBtn = this.root.querySelector<HTMLButtonElement>("#match-end-back");
    if (backBtn) {
      backBtn.addEventListener("mouseenter", () => (backBtn.style.filter = "brightness(1.08)"));
      backBtn.addEventListener("mouseleave", () => (backBtn.style.filter = "none"));
      backBtn.addEventListener("mousedown", () => (backBtn.style.transform = "translateX(-50%) translateY(1px)"));
      backBtn.addEventListener("mouseup", () => (backBtn.style.transform = "translateX(-50%)"));
      backBtn.addEventListener("click", () => this.post({ type: "hupper-fps-exit" }));
    }

    // Let the embedding web app refresh the balance / show its own result.
    this.post({ type: "hupper-fps-match-end", result: msg });

    // A settlement that resolved during the grace window (before the overlay
    // was up) was buffered — render its final line now.
    if (this.pendingSettle) {
      this.applySettleResult(this.pendingSettle.kind, this.pendingSettle.detail);
      this.pendingSettle = null;
    }
  }

  /** Update the escrow line once the on-chain settlement resolves. */
  setSettleResult(kind: "ok" | "failed", detail?: string): void {
    // The overlay is held back during the post-match grace window — if the
    // settle lands first, buffer it so show() can render the final line.
    if (!this.shown) {
      this.pendingSettle = { kind, detail };
      return;
    }
    this.applySettleResult(kind, detail);
  }

  private applySettleResult(kind: "ok" | "failed", detail?: string): void {
    const el = this.root.querySelector<HTMLDivElement>("#match-end-escrow");
    if (!el) return;
    if (kind === "ok") {
      el.textContent = this.won ? "Paid to your wallet ✓" : "Match settled.";
      el.style.color = this.won ? "#34D399" : "rgba(255,255,255,0.6)";
    } else {
      el.textContent = `Settlement failed${detail ? ` — ${detail}` : ""}. Stakes stay escrowed.`;
      el.style.color = "#EF4444";
    }
    // Nudge the host to refresh the wallet balance after a successful pay.
    if (kind === "ok") this.post({ type: "hupper-fps-settled" });
  }

  private post(payload: unknown): void {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, "*");
      } else {
        // Standalone (practice mode): no embedding host, so deliver to this
        // window instead — main.ts listens for `hupper-fps-exit` there and
        // restarts into a fresh practice match. No listener → harmless no-op,
        // same as before.
        window.postMessage(payload, "*");
      }
    } catch {
      /* cross-origin without access */
    }
  }
}
